import { Box, Container, Flex, Text, Heading, Grid } from '@radix-ui/themes';
import { StarFilledIcon } from '@radix-ui/react-icons';
import { Image } from '@trashlab/sitesapp/src/organizations/piedmont/components/shared/image';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/piedmont/contexts/use-tenant-context';
import { useState, useRef, useEffect } from 'react';
import { trpcWebsiteGetGoogleReviews } from '@trashlab/api/src/generated/hooks';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

export const Testimonials = ({
  withBackground = false,
}: {
  withBackground?: boolean;
}) => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);
  const city = orgAddress?.city ?? 'San Francisco';
  const state = orgAddress?.state ?? 'CA';
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { data: googleData, isLoading } = trpcWebsiteGetGoogleReviews.useQuery(
    {
      placeId: website?.googlePlaceId ?? '',
      limit: 4,
      minRating: 4,
    },
    {
      enabled: !!(website?.googlePlaceId && website?.enableGoogleReviews),
      staleTime: 60 * 60 * 1000,
    },
  );

  // Reset refs array when reviews change
  useEffect(() => {
    if (googleData?.reviews) {
      cardRefs.current = cardRefs.current.slice(0, googleData.reviews.length);
    }
  }, [googleData?.reviews]);

  // Calculate and set maximum card height
  useEffect(() => {
    if (typeof window !== 'undefined' && cardRefs.current.length > 0) {
      // Allow layout to settle
      const timer = setTimeout(() => {
        const heights = cardRefs.current
          .filter(Boolean)
          .map(ref => ref?.offsetHeight || 0);
        
        if (heights.length > 0) {
          const maxHeight = Math.max(...heights);
          setCardHeight(maxHeight);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [googleData?.reviews]);

  if (!website?.googlePlaceId || !website?.enableGoogleReviews) {
    return null;
  }

  if (isLoading && website?.organization?.id) {
    return (
      <Container size="4" p="8">
        <Flex direction="column" align="center" gap="2">
          <Text>Loading reviews...</Text>
        </Flex>
      </Container>
    );
  }

  const googleReviewsHidden =
    !googleData ||
    !googleData.reviews ||
    googleData.reviews.length === 0 ||
    (googleData.totalReviews && googleData.totalReviews < 10);

  if (googleReviewsHidden) {
    return null;
  }

  return (
    <Container
      size="4"
      p="8"
      className={
        withBackground
          ? `relative min-h-[200px] overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[url('/default/rental-steps-bg.svg')] before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-10 after:content-[''] after:absolute after:inset-0 after:opacity-90 after:-z-10`
          : ''
      }
    >
      <Flex direction="column" align="center" gap="2">
        <Text size="2" weight="bold" className="text-neutral-400 text-center">
          Testimonials
        </Text>
        <Heading as="h2" size="8" className="text-gray-900 text-center max-w-[793px]">
          {googleData?.businessName ? (
            <>
              Reviews for {googleData.businessName} in {city}, {state}
            </>
          ) : (
            <>
              Customer testimonials for dumpster rental in {city}, {state}
            </>
          )}
        </Heading>

        {googleData?.averageRating && (
          <Flex direction="column" align="center" gap="1" className="mt-2">
            <Flex className="items-center gap-2">
              <Flex direction="row">
                {[...Array(5)].map((_, i) => (
                  <StarFilledIcon
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.round(googleData.averageRating ?? 0)
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </Flex>
              <Text className="font-bold">
                {googleData.averageRating.toFixed(1)}
              </Text>
            </Flex>
            <Text size="2" className="text-neutral-600">
              Based on {googleData.totalReviews} reviews
            </Text>
          </Flex>
        )}

        <Box className="z-10 mt-6 w-full hidden lg:block">
          <Grid columns="2" gap="4">
            {googleData.reviews.map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </Grid>
        </Box>

        {/* Mobile horizontal scroll */}
        <Box className="z-10 mt-6 w-full lg:hidden">
          <Box 
            className="overflow-x-auto pb-4 relative -mx-8 hide-scrollbar" 
            style={{ 
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              paddingLeft: '8px',
              paddingRight: '0'
            }}
          >
            <Flex gap="4" className="w-max pl-0 ml-0">
              {googleData.reviews.map((testimonial, index) => (
                <Box 
                  key={index} 
                  className="w-[75vw] max-w-[290px] flex-shrink-0"
                >
                  <div 
                    ref={el => { cardRefs.current[index] = el; }}
                    style={{ 
                      height: cardHeight ? `${cardHeight}px` : 'auto',
                    }}
                  >
                    <TestimonialCard {...testimonial} />
                  </div>
                </Box>
              ))}
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Container>
  );
};

const TestimonialCard = ({
  name,
  date,
  content,
  rating = 5,
}: (typeof trpcWebsiteGetGoogleReviews.output)['reviews'][number]) => {
  const googleIcon = '/default/google-logo.svg';

  return (
    <Flex
      direction="column"
      justify="between"
      gap="4"
      className="relative overflow-hidden p-4 shadow-xl border border-gray-200 bg-white rounded-lg h-full"
    >
      <Flex justify="between" align="center" gap="4">
        <Flex align="center" gap="3">
          <Heading as="h2" size="5" className="text-gray-900 tracking-wide">
            {name}
          </Heading>
        </Flex>
        <Text size="3" className="text-zinc-600 whitespace-nowrap">
          {date}
        </Text>
      </Flex>

      <Box className="flex-grow">
        <Text size="3" className="text-gray-900 tracking-wide leading-7">
          {content}
        </Text>
      </Box>

      <Flex direction="row" justify="between" align="center" className="mt-auto">
        <Flex direction="row">
          {[...Array(5)].map((_, i) => (
            <StarFilledIcon
              key={i}
              className={`${
                i < rating ? 'text-yellow-400' : 'text-gray-300'
              } w-5 h-5`}
            />
          ))}
        </Flex>
        <Image src={googleIcon} alt="Google Reviews" width={50} height={50} />
      </Flex>
    </Flex>
  );
};

export default Testimonials;
